<form id="content" [formGroup]="form" (ngSubmit)="save()">
  <div class="page-title sticky d-flex align-items-center justify-content-between flex-wrap mb-4">
    <h1 class="mb-0">
      <a class="page-title-root" routerLink="/employees/rrhh/surveys">Encuestas</a>
      @if (breadcrumb_survey_title(); as survey_title) {
        <span class="page-title-separator">/</span>
        <span class="page-title-crumb" [attr.title]="survey_title">{{ survey_title }}</span>
      }
    </h1>
    <div class="page-actions d-flex align-items-center gap-2">
      <app-pending-changes-save-status [status_state]="pending_changes_save_status" />
      <button mat-flat-button color="accent" type="submit" [disabled]="loading()">
        <mat-icon class="survey-edit-action-icon">save</mat-icon>
        Guardar
      </button>
    </div>
  </div>

  <fieldset class="survey-edit-fieldset" [disabled]="loading()">
    <div class="survey-edit-layout">
      <div class="survey-edit-sidebar">
        <div class="survey-general-config-card">
          <h2 class="survey-general-config-card__title">Configuración general</h2>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Título</mat-label>
            <input matInput formControlName="title" />
            @if (form.controls.title.hasError('required')) {
              <mat-error>El título es obligatorio</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Estado</mat-label>
            <mat-select formControlName="status">
              @for (status_option of status_options; track status_option.value) {
                <mat-option [value]="status_option.value">{{ status_option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Frecuencia de envío</mat-label>
            <mat-select formControlName="frequency">
              @for (option of frequency_options; track option.value) {
                <mat-option [value]="option.value">{{ option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <div class="survey-general-config-dates">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Desde</mat-label>
              <input matInput type="date" formControlName="start_date" />
              @if (form.controls.start_date.hasError('required')) {
                <mat-error>La fecha de inicio es obligatoria</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Hasta</mat-label>
              <input matInput type="date" formControlName="end_date" />
              @if (form.controls.end_date.hasError('required')) {
                <mat-error>La fecha de fin es obligatoria</mat-error>
              }
              @if (form.controls.end_date.hasError('invalidDate')) {
                <mat-error>La fecha no es válida</mat-error>
              }
              @if (form.controls.end_date.hasError('dateAfterStart')) {
                <mat-error>La fecha de fin debe ser posterior a la fecha de inicio</mat-error>
              }
            </mat-form-field>
          </div>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Días posponibles</mat-label>
            <input matInput type="number" min="0" formControlName="postponable_days" />
          </mat-form-field>
        </div>

        <section class="survey-scope-card">
          <div class="survey-scope-card__header">
            <div class="survey-scope-card__heading">
              <h2 class="survey-scope-card__title">Configuración de Alcance</h2>
              <p class="survey-scope-card__subtitle">Esta configuración se heredará a todas las secciones de la encuesta</p>
            </div>
          </div>

          <div class="survey-scope-card__blocks">
            <div class="survey-audience-group">
              <app-targeted-roles-card
                title="1. A quién preguntar (Roles)"
                description="Define qué roles recibirán esta encuesta"
                all_label="Todos los roles"
                [embedded]="true"
                [options]="roles()"
                [selection]="form.controls.role_ids.value"
                [error_message]="roles_error()"
                (selectionChange)="onRoleIdsChange($event)"
              />

              @if (worker_role_selected()) {
                <app-targeted-department-categories-card
                  class="survey-audience-group__department-categories"
                  title="Categorías de departamento"
                  description="Filtra por categorías de departamento del rol worker"
                  [embedded]="true"
                  [options]="department_category_options()"
                  [selection]="form.controls.department_category_ids.value"
                  [error_message]="department_categories_error()"
                  (selectionChange)="onDepartmentCategoryIdsChange($event)"
                />
              }
            </div>
          </div>
        </section>
      </div>

      <section class="survey-sections-card">
        <div class="survey-sections-card__header">
          <h2 class="survey-sections-card__title">Secciones</h2>
          <button mat-stroked-button type="button" [disabled]="loading()" (click)="openAddSectionDialog()">
            <mat-icon class="survey-edit-action-icon">add</mat-icon>
            Añadir
          </button>
        </div>

        @if (sections().length > 0) {
          <div class="survey-sections-list" cdkDropList [cdkDropListDisabled]="loading()" (cdkDropListDropped)="dropSection($event)">
            @for (section of sections(); track section.id !== null ? 'saved-' + section.id : 'draft-' + section.order; let index = $index) {
              <article class="survey-section-item" cdkDrag [cdkDragDisabled]="loading()">
                <button class="survey-section-item__drag" type="button" cdkDragHandle [disabled]="loading()" aria-label="Reordenar sección">
                  <mat-icon>drag_indicator</mat-icon>
                </button>

                <div class="survey-section-item__body">
                  <div class="survey-section-item__top">
                    <div class="survey-section-item__heading">
                      <h3 class="survey-section-item__title">{{ index + 1 }}. {{ section.title }}</h3>
                      <span class="survey-section-item__badge">{{ section.badge_label }}</span>
                    </div>

                    <div class="survey-section-item__actions">
                      <button
                        mat-icon-button
                        type="button"
                        [disabled]="loading()"
                        aria-label="Configurar"
                        matTooltip="Configurar"
                        (click)="openSectionSettings(section)"
                      >
                        <mat-icon>settings</mat-icon>
                      </button>
                      <button
                        class="survey-section-item__delete"
                        mat-icon-button
                        type="button"
                        [disabled]="loading()"
                        aria-label="Eliminar"
                        matTooltip="Eliminar"
                        (click)="removeSection(section)"
                      >
                        <mat-icon>delete</mat-icon>
                      </button>
                    </div>
                  </div>

                  <p class="survey-section-item__description">{{ section.description }}</p>
                  <p class="survey-section-item__meta">{{ section.meta_label }}</p>
                </div>
              </article>
            }
          </div>
        }
      </section>
    </div>
  </fieldset>
</form>
